<script setup>
import {ref, onMounted, watch, computed} from 'vue'
import {useFloating, autoUpdate, offset, arrow, shift, autoPlacement} from '@floating-ui/vue'
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import DefaultTextarea from "@/components/global-themed/inputs/DefaultTextarea.vue";
import {onClickOutside} from "@vueuse/core/index";

const props = defineProps({
  feedbackItem: {
    type: Object,
    required: true,
  },
  theme: {
    type: String,
    default: 'default',
  }
})

const emit = defineEmits(['update', 'cancel', 'delete'])

const boundElement = ref(null)
const tooltipElement = ref(null)
const arrowElement = ref(null)
const arrowStyle = ref({})
const opacityClass = ref('tw-opacity-1')

const feedback = ref('')
const editing = computed(() => !!props.feedbackItem.feedback)

onClickOutside(tooltipElement, () => emit('cancel'));

onMounted(() => {
  boundElement.value = props.feedbackItem.feedbackElement
  feedback.value = props.feedbackItem.feedback
})

watch(
  () => props.feedbackItem.feedbackElement,
  (newVal) => {
    boundElement.value = newVal
  }
)

const {
  middlewareData,
  placement,
  floatingStyles,
} = useFloating(boundElement, tooltipElement, {
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(20),
    arrow({
      element: arrowElement,
      padding: 10
    }),
    shift({
      padding: 5,
    }),
    autoPlacement({
      allowedPlacements: ['top', 'bottom'],
    })
  ],
})

watch(middlewareData, () => {
  updateArrowPosition()
}, { deep: true })

const updateArrowPosition = () => {
  const side = placement.value.split("-")[0]

  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  }[side]

  const {arrow: arrowData} = middlewareData.value

  if (arrowData && arrowElement.value) {
    const { x } = arrowData
    const arrowLen = 30 // Fixed arrow size instead of relying on offsetWidth

    arrowStyle.value = {
      left: x ? `${x}px` : '',
      top: '',
      // Ensure the static side gets unset when flipping to other placements' axes.
      right: '',
      bottom: '',
      [staticSide]: `${-arrowLen / 2}px`,
    }
  }
}

const arrowClass = computed(() => {
  switch (placement.value) {
    case 'bottom':
      return 'tw-border-l tw-border-t';
    case 'left':
      return 'tw-border-t tw-border-r';
    case 'top':
      return 'tw-border-r tw-border-b';
    case 'right':
      return 'tw-border-b tw-border-l';
    default:
      return '';
  }
})
</script>

<template>
  <div
    id="feedback-tooltip"
    ref="tooltipElement"
    :style="floatingStyles"
    :class="[opacityClass]"
    class="
      tw-bg-white
      tw-text-neutral-700
      tw-border
      tw-border-neutral
      tw-p-6
      tw-inline-block
      tw-text-base
      tw-rounded-md
      tw-absolute
      tw-drop-shadow-behind
      tw-transition-opacity
      tw-delay-300
      tw-w-[480px]
      tw-z-10
    "
  >
    <div class="tw-flex tw-flex-col tw-gap-4">
      <DefaultTextarea
        v-model="feedback"
        :auto-expand="true"
        placeholder="Tell me how I can improve this for you"
        icon-path="icons/chat-message"
      />
      <div class="tw-flex tw-items-center tw-justify-end">
        <button
          v-if="editing"
          class="tw-mr-auto tw-underline tw-text-primary hover:tw-text-danger tw-cursor-pointer tw-transition-colors"
          @click="emit('delete')"
        >
          Delete
        </button>
        <div class="tw-flex tw-gap-3">
          <DefaultButton
            class="tw-px-6 tw-py-2 tw-rounded-lg"
            size="auto"
            variant="primary-inverted"
            @click="emit('cancel')"
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            class="tw-px-6 tw-py-2 tw-rounded-lg"
            size="auto"
            :disabled="!feedback"
            @click="emit('update', feedback)"
          >
            {{ editing ? 'Update' : 'Comment' }}
          </DefaultButton>
        </div>
      </div>
    </div>

    <div
      ref="arrowElement"
      :style="arrowStyle"
      :class="[opacityClass, arrowClass]"
      class="
        tw-bg-white
        tw-border-neutral
        tw-absolute
        tw-rotate-45
        tw-transition-opacity
        tw-delay-300
        tw-w-7
        tw-h-7
      "
    />
  </div>
</template>
